/**
 * PTD-10 — Team-Drill Leaderboard
 *
 * Polls a team_parent job until terminal, then fetches each child's
 * EvaluationResult to read overall_score + metadata.focal_figure_label.
 * Cards are sorted by overall_score descending once available.
 */
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { getJobStatus, getEvaluation, getSquadOrchestration } from '@/services/evaluationApi';
import type { ChildJobSummary } from '@/services/evaluationApi';
import type { EvaluationResult } from '@/types/api';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
  Badge,
} from '@/components/ui';
import { SquadScoreCard } from '@/components/evaluation';

// ─── Terminal status helpers ──────────────────────────────────────────────────

const TERMINAL_STATUSES = new Set(['completed', 'failed']);

function isTerminal(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/** Polling interval in ms — matches solo eval tiered strategy. */
function parentPollingInterval(elapsedMs: number): number {
  if (elapsedMs < 10_000) return 2_000;
  if (elapsedMs < 60_000) return 3_000;
  if (elapsedMs < 180_000) return 5_000;
  return 10_000;
}

// ─── Score colour ─────────────────────────────────────────────────────────────

function scoreColour(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-amber-400';
  return 'text-red-400';
}

// ─── Child card ───────────────────────────────────────────────────────────────

interface ChildCardProps {
  child: ChildJobSummary;
  evalResult: EvaluationResult | undefined;
  rank: number | null;
}

function ChildCard({ child, evalResult, rank }: ChildCardProps) {
  const focalLabel = evalResult?.metadata?.focal_figure_label;
  // Use focal_figure_label when available; fall back to "Person N" (1-based)
  const displayName = focalLabel
    ? focalLabel
    : `Person ${child.team_person_index + 1}`;

  const score =
    evalResult?.overall_score != null
      ? Math.round(evalResult.overall_score > 1 ? evalResult.overall_score : evalResult.overall_score * 100)
      : null;

  const isLoading =
    child.status === 'queued' || child.status === 'processing';
  const isFailed = child.status === 'failed';
  const hasResult = !!child.result_evaluation_id && score != null;

  return (
    <div
      className="relative rounded-xl border border-border-subtle bg-dark-elevated-1 overflow-hidden"
      data-testid={`child-card-${child.team_person_index}`}
    >
      {/* Rank badge — only once a score is available */}
      {rank != null && (
        <div
          className="absolute top-3 right-3 w-7 h-7 rounded-full bg-dark-elevated-2 border border-border-subtle flex items-center justify-center text-xs font-bold text-text-secondary"
          aria-label={`Rank ${rank}`}
          data-testid={`rank-badge-${child.team_person_index}`}
        >
          {rank}
        </div>
      )}

      <div className="p-5">
        {/* Card header: label + score */}
        <div className="flex items-baseline gap-3 mb-3 pr-8">
          <span
            className="font-bold text-lg text-text-primary"
            data-testid={`person-label-${child.team_person_index}`}
          >
            {displayName}
          </span>
          {score != null && (
            <span
              className={`text-2xl font-bold tabular-nums ${scoreColour(score)}`}
              data-testid={`person-score-${child.team_person_index}`}
            >
              {score}
            </span>
          )}
        </div>

        {/* Status indicators */}
        {isLoading && !hasResult && (
          <div
            className="flex items-center gap-2 text-sm text-text-secondary"
            data-testid={`loading-skeleton-${child.team_person_index}`}
            aria-live="polite"
          >
            <Spinner size="sm" />
            <span>
              {child.status === 'queued' ? 'Queued...' : 'Scoring...'}
              {child.progress_pct != null && ` ${Math.round(child.progress_pct)}%`}
            </span>
          </div>
        )}

        {isFailed && (
          <div
            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-red-500/15 text-red-400 border border-red-500/30"
            data-testid={`error-chip-${child.team_person_index}`}
          >
            <span>Failed</span>
            {child.error_message && (
              <span className="text-red-400/70" title={child.error_message}>
                — {child.error_message.length > 60 ? child.error_message.slice(0, 57) + '...' : child.error_message}
              </span>
            )}
          </div>
        )}

        {/* View details link — only when result is available */}
        {child.result_evaluation_id && (
          <div className="mt-3">
            <Link
              to={`/evaluations/${child.result_evaluation_id}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
              data-testid={`view-details-${child.team_person_index}`}
            >
              View details
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Aggregate progress bar ───────────────────────────────────────────────────

interface AggregateProgressProps {
  children: ChildJobSummary[];
  parentStatus: string;
}

function AggregateProgress({ children, parentStatus }: AggregateProgressProps) {
  const scored = children.filter(
    (c) => c.status === 'completed' || c.status === 'scored' || c.status === 'failed'
  ).length;
  const total = children.length;

  // Once parent is terminal, show 100%
  const pct = isTerminal(parentStatus) ? 100 : total > 0 ? Math.round((scored / total) * 100) : 0;

  return (
    <div className="space-y-1.5" data-testid="aggregate-progress">
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span>
          {scored} of {total} players scored
        </span>
        <span>{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-dark-elevated-2 overflow-hidden">
        <div
          className="h-full rounded-full bg-accent transition-all duration-500"
          style={{ width: `${pct}%` }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function TeamDrillResults() {
  const { parentJobId } = useParams<{ parentJobId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Poll the parent job ──────────────────────────────────────────────────────
  const parentQuery = useQuery({
    queryKey: ['evaluation-job', parentJobId],
    queryFn: () => getJobStatus(parentJobId!),
    enabled: !!parentJobId,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry 401s
      const status = (error as { response?: { status?: number } })?.response?.status;
      return status !== 401 && failureCount < 2;
    },
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data || isTerminal(data.status)) return false;
      const elapsed = Date.now() - new Date(data.submitted_at).getTime();
      return parentPollingInterval(elapsed);
    },
  });

  const parentJob = parentQuery.data;
  const childJobs: ChildJobSummary[] = parentJob?.child_jobs ?? [];

  // Invalidate eval list caches when parent completes so dashboards refresh.
  useEffect(() => {
    if (parentJob && isTerminal(parentJob.status)) {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['my-evaluations'] });
    }
  }, [parentJob, parentJob?.status, queryClient]);

  // ── Fetch individual eval results for each child that has one ───────────────
  // One useQuery per child with a result_evaluation_id.
  // Because hooks must be called unconditionally, we build a fixed-size array
  // outside the render. We cap at 5 (matching backend max_persons cap).
  const MAX_CHILDREN = 5;

  // Pad child array to MAX_CHILDREN so hook calls are stable
  const paddedChildren: (ChildJobSummary | null)[] = Array.from(
    { length: MAX_CHILDREN },
    (_, i) => childJobs[i] ?? null
  );

  const childEvalQueries = [
    useQuery({
      queryKey: ['evaluation', paddedChildren[0]?.result_evaluation_id],
      queryFn: () => getEvaluation(paddedChildren[0]!.result_evaluation_id!),
      enabled: !!paddedChildren[0]?.result_evaluation_id,
      staleTime: 60_000,
    }),
    useQuery({
      queryKey: ['evaluation', paddedChildren[1]?.result_evaluation_id],
      queryFn: () => getEvaluation(paddedChildren[1]!.result_evaluation_id!),
      enabled: !!paddedChildren[1]?.result_evaluation_id,
      staleTime: 60_000,
    }),
    useQuery({
      queryKey: ['evaluation', paddedChildren[2]?.result_evaluation_id],
      queryFn: () => getEvaluation(paddedChildren[2]!.result_evaluation_id!),
      enabled: !!paddedChildren[2]?.result_evaluation_id,
      staleTime: 60_000,
    }),
    useQuery({
      queryKey: ['evaluation', paddedChildren[3]?.result_evaluation_id],
      queryFn: () => getEvaluation(paddedChildren[3]!.result_evaluation_id!),
      enabled: !!paddedChildren[3]?.result_evaluation_id,
      staleTime: 60_000,
    }),
    useQuery({
      queryKey: ['evaluation', paddedChildren[4]?.result_evaluation_id],
      queryFn: () => getEvaluation(paddedChildren[4]!.result_evaluation_id!),
      enabled: !!paddedChildren[4]?.result_evaluation_id,
      staleTime: 60_000,
    }),
  ];

  // Build result map: team_person_index → EvaluationResult
  const evalResultByIndex = new Map<number, EvaluationResult | undefined>();
  for (let i = 0; i < MAX_CHILDREN; i++) {
    const child = paddedChildren[i];
    if (child) {
      evalResultByIndex.set(child.team_person_index, childEvalQueries[i].data);
    }
  }

  // ── Fetch squad orchestration result (positional team-drills only) ──────────
  // Only fires once the parent job is completed.  A 404 response means the
  // squad result doesn't exist yet (still writing) OR this is not a positional
  // team-drill — in both cases we treat it as "unavailable" and show nothing.
  const parentIsCompleted = parentJob?.status === 'completed';

  const squadQuery = useQuery({
    queryKey: ['squad-orchestration', parentJobId],
    queryFn: async () => {
      try {
        return await getSquadOrchestration(parentJobId!);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          // 404 = not available (not a positional drill, or result not yet written)
          return null;
        }
        throw err;
      }
    },
    enabled: !!parentJobId && parentIsCompleted,
    // Squad result is stable once written — no need to re-fetch.
    staleTime: Infinity,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // Don't retry 404s or 401s.
      return status !== 404 && status !== 401 && failureCount < 1;
    },
  });

  const squadResult = squadQuery.data ?? null;

  // ── Sort children by score (desc) for leaderboard order ─────────────────────
  const sortedChildren = [...childJobs].sort((a, b) => {
    const scoreA = evalResultByIndex.get(a.team_person_index)?.overall_score;
    const scoreB = evalResultByIndex.get(b.team_person_index)?.overall_score;
    if (scoreA == null && scoreB == null) return a.team_person_index - b.team_person_index;
    if (scoreA == null) return 1;
    if (scoreB == null) return -1;
    return scoreB - scoreA;
  });

  // ── Loading / error states ───────────────────────────────────────────────────
  if (!parentJobId) {
    return (
      <div className="text-center py-12">
        <p className="text-text-secondary">No team drill job specified.</p>
        <button
          type="button"
          onClick={() => navigate('/evaluations')}
          className="mt-4 text-sm text-accent underline"
        >
          Back to evaluations
        </button>
      </div>
    );
  }

  if (parentQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" label="Loading team drill results..." />
      </div>
    );
  }

  if (parentQuery.isError || !parentJob) {
    return (
      <div className="text-center py-12">
        <p className="text-text-secondary">Could not load team drill results.</p>
        <button
          type="button"
          onClick={() => navigate('/evaluations')}
          className="mt-4 text-sm text-accent underline"
        >
          Back to evaluations
        </button>
      </div>
    );
  }

  const isStillRunning = !isTerminal(parentJob.status);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Team Drill Results"
        description={
          isStillRunning
            ? 'Scoring in progress...'
            : parentJob.status === 'failed'
            ? 'Team drill failed'
            : 'Final leaderboard'
        }
        showBack
      />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Aggregate progress */}
        {(isStillRunning || childJobs.length > 0) && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Progress</CardTitle>
                {isStillRunning && (
                  <Badge variant="secondary" className="flex items-center gap-1.5">
                    <Spinner size="sm" />
                    In progress
                  </Badge>
                )}
                {!isStillRunning && parentJob.status === 'completed' && (
                  <Badge variant="success">Complete</Badge>
                )}
                {!isStillRunning && parentJob.status === 'failed' && (
                  <Badge variant="error">Failed</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <AggregateProgress children={childJobs} parentStatus={parentJob.status} />
            </CardContent>
          </Card>
        )}

        {/* Squad Orchestration Card — positional team-drills only, above leaderboard */}
        {squadResult && (
          <SquadScoreCard result={squadResult} />
        )}

        {/* Leaderboard */}
        {childJobs.length > 0 && (
          <div data-testid="leaderboard">
            <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3">
              Leaderboard
            </h2>
            <div className="space-y-3">
              {sortedChildren.map((child, idx) => {
                const evalResult = evalResultByIndex.get(child.team_person_index);
                const hasScore = evalResult?.overall_score != null;
                return (
                  <ChildCard
                    key={child.job_id}
                    child={child}
                    evalResult={evalResult}
                    rank={hasScore ? idx + 1 : null}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Empty state */}
        {childJobs.length === 0 && isStillRunning && (
          <div className="text-center py-8 text-text-secondary text-sm">
            <Spinner size="sm" className="mx-auto mb-3" />
            <p>Waiting for child jobs to appear...</p>
          </div>
        )}

        {childJobs.length === 0 && !isStillRunning && (
          <div className="text-center py-8 text-text-secondary text-sm">
            <p>No child jobs found for this team drill.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamDrillResults;
